'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function KoltukAltiTerlemesiIcinBotoksTedavisiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/koltuk-alti-terlemesi-icin-botoks-tedavisi';
  const pageTitle = 'Koltuk Altı Terlemesi İçin Botoks Tedavisi';
  const pageDescription = 'Koltuk altı terlemesi (aksiller hiperhidroz) botoks ile nasıl tedavi edilir? Etkisi ne kadar sürer, kimlere uygulanabilir? Tüm detaylar burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Koltuk Altı Terlemesi Botoks Tedavisi', href: '/tr/blog/koltuk-alti-terlemesi-icin-botoks-tedavisi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Koltuk altı terlemesi botoks tedavisi ve aksiller hiperhidroz çözümü değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Aksiller Hiperhidroz
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Koltuk Altı Terlemesi İçin Botoks Tedavisi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>1 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Koltuk altı terlemesi, tıbbi adıyla aksiller hiperhidroz, özellikle yaz aylarında hem sosyal hem de psikolojik rahatsızlık yaratan yaygın bir durumdur. Botoks tedavisi, ter bezlerinin aşırı aktivitesini durdurarak etkili, kalıcı ve cerrahi olmayan bir çözüm sunar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Yalnızca 15–20 dakikalık bir işlemle, kişi aylarca terlemeden özgür bir yaşam sürebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Koltuk Altı Terlemesi Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Koltuk altındaki ter bezleri, vücut ısısını dengelemek için ter üretir. Ancak bazı kişilerde bu bezler aşırı aktif hale gelir. Sonuç olarak;
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Günde defalarca kıyafet değiştirme</li>
                  <li>Ter kokusu ve ıslaklık hissi</li>
                  <li>Sosyal ortamlarda özgüven kaybı oluşur</li>
                </ul>
                <p className="text-red-800 mt-3 font-medium">
                  Botoks, bu bezlerin uyarı sinyallerini bloke ederek ter üretimini %90 oranında azaltır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Botoks Tedavisi Nasıl Etki Eder?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum toksin, sinir uçlarından asetilkolin adlı iletici maddenin salınımını geçici olarak durdurur. Bu sayede ter bezlerine "terle" sinyali ulaşmaz.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Etki yalnızca koltuk altı ter bezlerinde olur, vücudun diğer bölgelerindeki terleme sistemi etkilenmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Koltuk altı bölgesi temizlenir ve lokal anestezik krem uygulanır.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-purple-800 space-y-2">
                  <li>Koltuk altı bölgesi temizlenir ve lokal anestezik krem uygulanır</li>
                  <li>Nişasta-iyot testiyle terleme alanları belirlenir</li>
                  <li>Her koltuk altına ortalama 20–25 küçük enjeksiyon noktası uygulanır</li>
                  <li>İşlem toplam 15–20 dakika sürer</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="text-purple-800 font-medium">🕒 Etki 3–5 gün içinde başlar, 1–2 hafta içinde maksimum düzeye ulaşır</p>
                  <p className="text-purple-800 font-medium">💎 Etki süresi ortalama 6–9 ay devam eder</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etkisi ve Kalıcılığı
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisinin başlama süresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 gün</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Tam etki</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–2 hafta</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık süresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–9 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Terleme azalması</td>
                      <td className="px-6 py-4 text-sm text-gray-900">%80–90 oranında</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sosyal yaşam dönüşü</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Aynı gün</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Düzenli uygulamalarda terleme kontrolü daha uzun sürer; bazı hastalarda 1 yıla kadar devam eder.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Botoksun Avantajları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>✅ Cerrahi müdahale gerektirmez</li>
                  <li>✅ Etkisi hızlı başlar</li>
                  <li>✅ Koku ve nem azalır</li>
                  <li>✅ Giysi lekelenmesi sona erer</li>
                  <li>✅ Günlük yaşama hemen dönüş mümkündür</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  Bu nedenle botoks, aşırı koltuk altı terlemesinde en güvenilir medikal çözüm olarak kabul edilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Günlük yaşamda aşırı terleme şikayeti olanlar</li>
                    <li>Roll-on, kremler veya medikal tedavilerden fayda görmeyenler</li>
                    <li>Cerrahi müdahaleden kaçınmak isteyenler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Gebelik ve emzirme döneminde olanlar</li>
                    <li>Nöromüsküler hastalığı bulunanlar</li>
                    <li>Enjeksiyon bölgesinde enfeksiyonu olan kişiler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>İlk 24 saat sauna, hamam, egzersiz önerilmez</li>
                  <li>4 saat boyunca koltuk altına baskı uygulanmamalı</li>
                  <li>Deodorant kullanımı 24 saat ertelenmelidir</li>
                  <li>Hafif batma veya kızarıklık normaldir, kısa sürede geçer</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Koltuk altı terlemesi botoks tedavisi ve aksiller hiperhidroz çözümü"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Koltuk altı botoksu ağrılı mı?</h3>
                  <p className="text-gray-700">Hayır. Anestezik krem uygulandığı için işlem oldukça konforludur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Ortalama 6–9 ay sürer. Düzenli seanslarda etki süresi 1 yıla kadar uzayabilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Terleme tamamen geçer mi?</h3>
                  <p className="text-gray-700">%80–90 oranında azalma sağlanır. Bazı hastalarda terleme tamamen durur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Başka bölgelerde terleme artar mı?</h3>
                  <p className="text-gray-700">Hayır, botoks sadece uygulandığı bölgedeki ter bezlerini etkiler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Her mevsim yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet. Yaz aylarında en sık tercih edilir, ancak her mevsim güvenle uygulanabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – "Botulinum Toxin for Axillary Hyperhidrosis"</li>
                <li>Türk Dermatoloji Derneği – Hiperhidroz Tanı ve Tedavi Kılavuzu</li>
                <li>Mayo Clinic – "Botox for Excessive Sweating"</li>
                <li>Harvard Health Publishing – "Understanding Hyperhidrosis Treatments"</li>
                <li>ISAPS Global Report 2024 – "Minimally Invasive Sweat Reduction Techniques"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#koltukaltıterlemesi</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#hiperhidroz</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#terlemeterapisi</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#botokstedavisi</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Koltuk Altı Terlemesi Botoks Tedavisi İçin Randevu Alın</h3>
              <p className="mb-6">Aksiller hiperhidroz sorununuz için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Koltuk altı terlemesi botoks tedavisi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/ayak-terlemesi-icin-botoks-tedavisi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Ayak Terlemesi İçin Botoks Tedavisi</h4>
              <p className="text-gray-600 text-sm">Plantar hiperhidroz tedavisi hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoks etki süresi ve kalıcılık hakkında detaylı bilgiler.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
